<template>
  <div class="preview" ref="mount" :aria-busy="!previewReady">
    <div v-if="!previewReady" class="preview__placeholder" aria-label="Загружаем персонажа">
      <span class="preview__head" :style="{ background: appearance.skinColor }"></span>
      <span class="preview__body" :style="{ background: appearance.clothesColor }"></span>
      <span class="preview__legs"></span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch } from "vue"
import { createCharacter } from "./characterBuilder"
import { attachAccessories, detachAccessories, type AttachedAccessory } from "./accessoryBuilder"
import type { PlayerAppearance } from "../shared/types"
import type { ShopItemDto } from "../shared/shopTypes"

const props = defineProps<{
  appearance: PlayerAppearance
  equipped?: ShopItemDto[]
  rotateSpeed?: number
}>()

const mount = ref<HTMLDivElement | null>(null)
const previewReady = ref(false)
let scene: any = null
let camera: any = null
let renderer: any = null
let parts: any = null
let attached: AttachedAccessory[] = []
let rafId: number | null = null
let startTime = 0
let disposed = false
let reducedMotion = false
let resizeObserver: ResizeObserver | null = null

declare const THREE: any

function buildScene() {
  if (!mount.value) return
  reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false
  const el = mount.value
  scene = new THREE.Scene()
  scene.background = null

  const width = el.clientWidth
  const height = el.clientHeight
  camera = new THREE.PerspectiveCamera(38, width / height, 0.1, 100)
  camera.position.set(0, 1.4, 3.4)
  camera.lookAt(0, 1.1, 0)

  renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.6))
  renderer.setSize(width, height)
  renderer.shadowMap.enabled = true
  renderer.shadowMap.type = THREE.PCFSoftShadowMap
  renderer.outputColorSpace = THREE.SRGBColorSpace
  renderer.toneMapping = THREE.ACESFilmicToneMapping
  renderer.toneMappingExposure = 1.08
  el.appendChild(renderer.domElement)
  previewReady.value = true

  const ambient = new THREE.AmbientLight(0xffffff, 0.52)
  scene.add(ambient)
  const fill = new THREE.HemisphereLight(0xDDEBFF, 0x9A7652, 0.8)
  scene.add(fill)
  const dir = new THREE.DirectionalLight(0xffe9c2, 1.35)
  dir.position.set(3, 5, 2)
  dir.castShadow = true
  dir.shadow.mapSize.set(512, 512)
  scene.add(dir)

  const plate = new THREE.Mesh(
    new THREE.CylinderGeometry(1.1, 1.1, 0.15, 24),
    new THREE.MeshStandardMaterial({ color: 0x6258D9, roughness: 0.9, metalness: 0 }),
  )
  plate.position.y = -0.08
  plate.receiveShadow = true
  scene.add(plate)

  rebuildCharacter()
  resizeObserver = new ResizeObserver(() => {
    if (!mount.value || !renderer || !camera) return
    const nextWidth = mount.value.clientWidth
    const nextHeight = mount.value.clientHeight
    if (!nextWidth || !nextHeight) return
    renderer.setSize(nextWidth, nextHeight)
    camera.aspect = nextWidth / nextHeight
    camera.updateProjectionMatrix()
    frameCharacter()
  })
  resizeObserver.observe(el)
  startTime = performance.now() / 1000

  const animate = () => {
    const t = performance.now() / 1000 - startTime
    if (parts?.group) {
      parts.group.rotation.y += reducedMotion ? 0 : (props.rotateSpeed ?? 0.01)
      const motion = reducedMotion ? 0 : 1
      const breath = Math.sin(t * 1.2) * 0.012 * motion
      const blinkClock = t % 5.2
      const blinkScale = reducedMotion || blinkClock > 0.13 ? 1 : 0.12
      parts.group.position.y = breath
      parts.body.scale.y = 1 + breath
      parts.leftEye.scale.y = blinkScale
      parts.rightEye.scale.y = blinkScale
      parts.leftArm.rotation.x = Math.sin(t * 0.6) * 0.025 * motion
      parts.rightArm.rotation.x = -parts.leftArm.rotation.x
    }
    for (const a of attached) {
      if (a.update) a.update(t)
    }
    renderer.render(scene, camera)
    rafId = requestAnimationFrame(animate)
  }
  animate()
}

function rebuildCharacter() {
  if (!scene) return
  if (parts?.group) {
    scene.remove(parts.group)
  }
  parts = createCharacter(props.appearance)
  scene.add(parts.group)
  applyEquipment()
  frameCharacter()
}

function frameCharacter() {
  if (!parts?.group || !camera || !mount.value) return
  parts.group.updateMatrixWorld(true)
  const box = new THREE.Box3().setFromObject(parts.group)
  if (box.isEmpty()) return
  const size = box.getSize(new THREE.Vector3())
  const center = box.getCenter(new THREE.Vector3())
  const verticalFov = THREE.MathUtils.degToRad(camera.fov)
  const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * camera.aspect)
  const verticalDistance = size.y / (2 * Math.tan(verticalFov / 2))
  const horizontalDistance = size.x / (2 * Math.tan(horizontalFov / 2))
  const distance = Math.max(verticalDistance, horizontalDistance) * 1.18
  camera.position.set(center.x, center.y + size.y * 0.015, box.max.z + distance)
  camera.near = Math.max(0.05, distance / 100)
  camera.far = Math.max(100, distance * 20)
  camera.lookAt(center.x, center.y, center.z)
  camera.updateProjectionMatrix()
  if (new URLSearchParams(window.location.search).has("previewAudit")) {
    ;(window as any).__pomestoPreviewAudit = { parts, attached, camera, renderer, scene }
  }
}

function applyEquipment() {
  if (!parts) return
  detachAccessories(attached)
  attached = []
  if (props.equipped?.length) {
    attached = attachAccessories(
      parts.anchors,
      props.equipped.map((it) => ({ id: it.id, slot: it.slot })),
      {
        skin: props.appearance.skinColor,
        hair: props.appearance.hairColor,
        clothes: props.appearance.clothesColor,
      },
    )
  }
  frameCharacter()
}

watch(
  () => ({ ...props.appearance }),
  () => rebuildCharacter(),
  { deep: true },
)

watch(
  () => props.equipped?.map((e) => e.id).join("|") ?? "",
  () => applyEquipment(),
)

onMounted(async () => {
  try {
    if (typeof (window as any).THREE === "undefined") {
      ;(window as any).THREE = await import("three")
    }
    if (!disposed) buildScene()
  } catch (e) {
    // The lightweight placeholder remains usable when WebGL is unavailable.
  }
})

onBeforeUnmount(() => {
  disposed = true
  resizeObserver?.disconnect()
  if ((window as any).__pomestoPreviewAudit?.parts === parts) {
    delete (window as any).__pomestoPreviewAudit
  }
  if (rafId) cancelAnimationFrame(rafId)
  if (renderer) {
    renderer.dispose?.()
    if (mount.value && renderer.domElement.parentNode === mount.value) {
      mount.value.removeChild(renderer.domElement)
    }
  }
})
</script>

<style scoped>
.preview {
  width: 100%;
  height: 160px;
  border-radius: 20px;
  background: linear-gradient(180deg, #fdeccf 0%, #f4c78a 100%);
  box-shadow: inset 0 -18px 30px rgba(122, 63, 29, 0.25);
  overflow: hidden;
  position: relative;
}
.preview__placeholder { position: absolute; inset: 0; display: grid; place-content: center; justify-items: center; z-index: 1; }
.preview__head { width: 48px; height: 48px; border-radius: 16px 16px 12px 12px; box-shadow: inset 0 -8px 0 rgba(41,43,51,.1); }
.preview__body { width: 76px; height: 64px; margin-top: -2px; border-radius: 16px 16px 8px 8px; }
.preview__legs { width: 62px; height: 36px; background: linear-gradient(90deg, #292b33 0 42%, transparent 42% 58%, #292b33 58%); }
.preview :deep(canvas) { position: absolute; inset: 0; width: 100% !important; height: 100% !important; }
@media (min-width: 900px) {
  .preview {
    height: 260px;
  }
}
</style>
